import { useMemo, useState } from "react";
import { Briefcase, Layers, Package } from "lucide-react";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { matchesItemSearch } from "../../UI/Utils/matchesItemSearch.ts";
import { FilterActions } from "../../UI/Table/Filters/FilterActions.tsx";
import { toggleListValue } from "../../UI/Table/Filters/toggleListValue.ts";
import { isTestItem } from "../../ProductService/isTestItem.ts";

type PurchasableItemOption = {
  id: string;
  name: string;
  variantName: string;
  priceInCents: number;
  isBundle: boolean;
};

const italianEurosWithThousandsSeparator = new Intl.NumberFormat("it-IT", {
  useGrouping: "always",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function displayedPriceOf(priceInCents: number): string {
  return `€${(priceInCents / 100).toFixed(2)}`;
}

function searchableFieldsOf(option: PurchasableItemOption): string[] {
  const priceInEuros = option.priceInCents / 100;
  return [
    option.id,
    option.name,
    option.variantName,
    displayedPriceOf(option.priceInCents),
    priceInEuros.toFixed(2).replace(".", ","),
    italianEurosWithThousandsSeparator.format(priceInEuros),
  ];
}

export type PurchasedItemFilterExtraOption = {
  id: string;
  label: string;
};

export function PurchasedItemFilter({
  value,
  onApply,
  close,
  extraOptions = [],
}: {
  value: string[];
  onApply: (productIds: string[]) => void;
  close: () => void;
  extraOptions?: PurchasedItemFilterExtraOption[];
}) {
  const { products } = useAllNormasTeachingProducts();
  const { bundles } = useAllNormasTeachingBundles();
  const [selected, setSelected] = useState(value);
  const [search, setSearch] = useState("");

  const options: PurchasableItemOption[] = useMemo(
    () =>
      [
        ...products.map((p) => ({
          id: p._id,
          name: p.productName,
          variantName: p.variantProductName,
          priceInCents: p.priceInCents.amount,
          isBundle: false,
        })),
        ...bundles.map((b) => ({
          id: b._id,
          name: b.bundleName,
          variantName: b.variantBundleName,
          priceInCents: b.priceInCents.amount,
          isBundle: true,
        })),
      ].filter((option) => !isTestItem(option.name, option.variantName)),
    [products, bundles],
  );
  const visibleOptions = options.filter((option) =>
    matchesItemSearch(search, searchableFieldsOf(option)),
  );
  const visibleExtraOptions = extraOptions.filter((option) =>
    matchesItemSearch(search, [option.label]),
  );

  const toggle = (id: string) =>
    setSelected((current) => toggleListValue(current, id));
  const apply = (productIds: string[]) => {
    onApply(productIds);
    close();
  };

  return (
    <div className="flex flex-col gap-2 px-2">
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Cerca per nome, ID o prezzo"
        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
      />
      <div className="max-h-72 overflow-y-auto flex flex-col gap-0.5">
        {visibleExtraOptions.map((option) => (
          <label
            key={option.id}
            className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={() => toggle(option.id)}
              className="w-4 h-4 shrink-0 accent-primary"
            />
            <Briefcase className="w-4 h-4 shrink-0 text-teal-600" />
            <span className="truncate">{option.label}</span>
          </label>
        ))}
        {visibleOptions.map((option) => (
          <label
            key={option.id}
            title={`${option.name} · ${option.variantName}`}
            className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1 text-sm text-gray-700 hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={() => toggle(option.id)}
              className="mt-0.5 w-4 h-4 shrink-0 accent-primary"
            />
            {option.isBundle ? (
              <Layers className="mt-0.5 w-4 h-4 shrink-0 text-purple-500" />
            ) : (
              <Package className="mt-0.5 w-4 h-4 shrink-0 text-blue-500" />
            )}
            <span className="min-w-0 flex-1 break-words">
              {option.variantName}
            </span>
            <span className="shrink-0 pl-2 text-xs text-gray-500 tabular-nums">
              {displayedPriceOf(option.priceInCents)}
            </span>
          </label>
        ))}
        {visibleOptions.length === 0 && visibleExtraOptions.length === 0 && (
          <p className="py-2 text-center text-sm text-gray-400">
            Nessun risultato
          </p>
        )}
      </div>
      <FilterActions
        canClear={value.length > 0}
        onClear={() => apply([])}
        onApply={() => apply(selected)}
      />
    </div>
  );
}
